
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Divider, Drawer, Dropdown, Empty, Input, List, Modal, Select, Spin, Tag, Typography, message } from 'antd';
import {
	AudioOutlined, BellOutlined, CalendarOutlined, CheckCircleOutlined, CloseCircleOutlined,
	CaretDownOutlined, CaretRightOutlined, CodeOutlined, DesktopOutlined, FilePdfOutlined, FileTextOutlined, FileWordOutlined, LoadingOutlined, MoreOutlined, SearchOutlined, SendOutlined, ShareAltOutlined,
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import logoHandBtn from '../assets/logo_hand_button.png';
import * as api from '../api/client';
import type {
	AgentPlan, AgentStep, AgentTask, Artifact,
	PlanData, Project, TaskEvent, TaskFollowUp, TaskLibraryItem, ToolCallLog,
} from '../types';
import type { GoogleUser } from '../App';

import TaskStatusHeader from './TaskStatusHeader';
import AgentStepTimeline from './AgentStepTimeline';
import ArtifactPanel from './ArtifactPanel';
import FinalResultPanel from './FinalResultPanel';
import AddToKnowledgeBaseButton from './AddToKnowledgeBaseButton';
import AgentWorkspaceView from './AgentWorkspaceView';
import JsonViewer from './JsonViewer';
import DurationBadge from './DurationBadge';
import { STEP_STATUS_COLOR, STEP_TYPE_COLOR } from './StatusBadge';
import { summarizeTaskPrompt } from '../taskPromptSummary';

const { Text, Title } = Typography;
const MAX_DOWNLOAD_FILENAME_LENGTH = 60;
const MAX_SHARE_TEXT_LENGTH = 280;

const ACTIVE_STATUSES = new Set(['RUNNING', 'PLANNING', 'PAUSED']);
const TERMINAL_TASK_STATUSES = new Set(['COMPLETED', 'FAILED', 'CANCELLED']);
const ENABLE_TASK_SSE = process.env.REACT_APP_ENABLE_TASK_SSE === 'true';

interface KbOption {
	id: string;
	name: string;
	description?: string;
	document_count?: number;
	chunk_count?: number;
	project_id?: string;
}

interface RecentTaskOption {
	id: string;
	goal: string;
}

interface SuggestedTaskStarter {
	id: string;
	knowledge_base_id: string;
	name: string;
	description?: string;
	suggested_prompt?: string;
	recommended_mode?: 'chat' | 'deep_search' | 'autonomous';
	suggested_knowledge_base_ids?: string[];
	tags?: string[];
}

interface Props {
	taskId: string;
	onBack: () => void;
	onOpenTask?: (taskId: string) => void;
	user?: GoogleUser | null;
	onLogout?: () => void;
	availableKbs?: KbOption[];
	recentTasks?: RecentTaskOption[];
	onScheduleTask?: (goal: string) => void;
}

interface DeepSearchThreadEntry {
	key: string;
	title: string;
	createdAt?: string;
	content?: string;
	status?: string;
	taskId?: string;
}

// ── SSH Terminal View ─────────────────────────────────────────────────────────

const SshTerminalView: React.FC<{ toolCalls: ToolCallLog[]; events: TaskEvent[]; stepId: string }> = ({ toolCalls, events, stepId }) => {
	const sshCalls = toolCalls.filter(tc => tc.tool_name === 'SSH_EXECUTE');
	const sshOutputEvents = events.filter(evt => evt.step_id === stepId && evt.event === 'ssh.output');
	const streamedStdout = sshOutputEvents
		.filter(evt => evt.data?.stream !== 'stderr')
		.map(evt => evt.data?.line)
		.filter(Boolean)
		.join('\n');
	const streamedStderr = sshOutputEvents
		.filter(evt => evt.data?.stream === 'stderr')
		.map(evt => evt.data?.line)
		.filter(Boolean)
		.join('\n');
	const hasRunningCall = sshCalls.some(tc => tc.status === 'PENDING');
	const hasPersistedOutput = sshCalls.some(tc => {
		try {
			const result = JSON.parse(tc.result_json || '{}');
			return Boolean(result?.output || result?.stdout || result?.error || result?.stderr);
		} catch {
			return false;
		}
	});

	return (
		<div>
			{sshCalls.map((tc, idx) => {
				let args: any = null;
				let result: any = null;
				try { args = JSON.parse(tc.arguments_json || '{}'); } catch { }
				try { result = JSON.parse(tc.result_json || '{}'); } catch { }

				const host = args?.host ?? '';
				const username = args?.username ?? '';
				const port = args?.port ?? 22;
				const command = args?.command ?? '';
				const stdout = result?.output ?? result?.stdout ?? (sshCalls.length === 1 ? streamedStdout : '');
				const stderr = result?.error ?? result?.stderr ?? (sshCalls.length === 1 ? streamedStderr : '');
				const exitCode = result?.exit_code;
				const status = result?.status ?? tc.status;
				const isRunning = tc.status === 'PENDING';
				const isFailed = tc.status === 'FAILED' || status === 'failure';

				return (
					<div key={tc.id} className="praxys-ssh-terminal" style={{ marginBottom: idx < sshCalls.length - 1 ? 12 : 0 }}>
						{/* Terminal title bar */}
						<div className="praxys-ssh-terminal-bar">
							<CodeOutlined style={{ color: '#22d3ee', fontSize: 12 }} />
							<span className="praxys-ssh-terminal-host">
								{username && host ? `${username}@${host}:${port}` : 'SSH Execution'}
							</span>
							{isRunning && <LoadingOutlined style={{ color: '#3b82f6', fontSize: 11 }} />}
							{!isRunning && (
								<span
									className="praxys-ssh-terminal-badge"
									style={{ color: isFailed ? '#ff7875' : '#52c41a' }}
								>
									{isFailed ? '✗ failed' : '✓ success'}
									{exitCode != null ? ` (exit ${exitCode})` : ''}
								</span>
							)}
						</div>

						{/* Command line */}
						{command && (
							<div className="praxys-ssh-terminal-cmd">
								<span className="praxys-ssh-terminal-prompt">$</span>
								<span className="praxys-ssh-terminal-command">{command}</span>
							</div>
						)}

						{/* stdout output */}
						{stdout && (
							<pre className="praxys-ssh-terminal-output">{stdout}</pre>
						)}

						{/* stderr output */}
						{stderr && (
							<pre className="praxys-ssh-terminal-output praxys-ssh-terminal-stderr">{stderr}</pre>
						)}

						{/* Running indicator */}
						{isRunning && !stdout && !stderr && (
							<div className="praxys-ssh-terminal-running">
								<LoadingOutlined style={{ marginRight: 6 }} />
								Connecting and executing…
							</div>
						)}
					</div>
				);
			})}
			{sshCalls.length === 0 && (streamedStdout || streamedStderr) && (
				<div className="praxys-ssh-terminal">
					<div className="praxys-ssh-terminal-bar">
						<CodeOutlined style={{ color: '#22d3ee', fontSize: 12 }} />
						<span className="praxys-ssh-terminal-host">SSH Execution Output</span>
					</div>
					{streamedStdout && (
						<pre className="praxys-ssh-terminal-output">{streamedStdout}</pre>
					)}
					{streamedStderr && (
						<pre className="praxys-ssh-terminal-output praxys-ssh-terminal-stderr">{streamedStderr}</pre>
					)}
				</div>
			)}
			{sshCalls.length > 1 && (hasRunningCall || !hasPersistedOutput) && (streamedStdout || streamedStderr) && (
				<div className="praxys-ssh-terminal">
					<div className="praxys-ssh-terminal-bar">
						<CodeOutlined style={{ color: '#22d3ee', fontSize: 12 }} />
						<span className="praxys-ssh-terminal-host">SSH Execution Output</span>
					</div>
					{streamedStdout && (
						<pre className="praxys-ssh-terminal-output">{streamedStdout}</pre>
					)}
					{streamedStderr && (
						<pre className="praxys-ssh-terminal-output praxys-ssh-terminal-stderr">{streamedStderr}</pre>
					)}
				</div>
			)}
		</div>
	);
};

// ── Praxys's Computer panel ───────────────────────────────────────────────────

const ComputerPanel: React.FC<{
	step: AgentStep;
	stepToolCalls: ToolCallLog[];
	stepArtifacts: Artifact[];
	events: TaskEvent[];
	taskId: string;
	userId: string;
	taskStatus: string;
	onClose: () => void;
	onPause: () => void;
	onResume: () => void;
}> = ({ step, stepToolCalls, stepArtifacts, events, taskId, userId, taskStatus, onClose, onPause, onResume }) => {
	let output: any = null;
	let input: any = null;
	try { output = JSON.parse(step.output_json || '{}'); } catch { }
	try { input = JSON.parse(step.input_json || '{}'); } catch { }
	const isEmpty = (o: any) => !o || (typeof o === 'object' && Object.keys(o).length === 0);

	const hasSshCalls = stepToolCalls.some(tc => tc.tool_name === 'SSH_EXECUTE')
		|| events.some(evt => evt.step_id === step.id && evt.event === 'ssh.output');

	return (
		<div className="praxys-computer-panel">
			{/* Header */}
			<div className="praxys-computer-header">
				<div className="praxys-computer-title">
					<DesktopOutlined style={{ color: '#10a37f', marginRight: 8 }} />
					Praxys's Computer
				</div>
				<button className="praxys-computer-close" onClick={onClose} aria-label="Close panel">
					✕
				</button>
			</div>

			{/* Body */}
			<div className="praxys-computer-body">
				{/* Step identity */}
				<div className="praxys-computer-section">
					<div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
						{step.status === 'RUNNING'
							? <LoadingOutlined style={{ color: '#3b82f6', fontSize: 16, marginTop: 2 }} />
							: step.status === 'COMPLETED'
								? <CheckCircleOutlined style={{ color: '#10b981', fontSize: 16, marginTop: 2 }} />
								: step.status === 'FAILED'
									? <CloseCircleOutlined style={{ color: '#ef4444', fontSize: 16, marginTop: 2 }} />
									: <BellOutlined style={{ color: '#faad14', fontSize: 16, marginTop: 2 }} />}
						<div>
							<Title level={5} style={{ color: '#ececec', margin: 0, fontSize: 14 }}>
								{step.name}
							</Title>
							<div style={{ marginTop: 4, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
								<span
									style={{
										display: 'inline-flex',
										alignItems: 'center',
										gap: 5,
										background: '#2a2a2a',
										border: '1px solid #3f3f3f',
										borderRadius: 20,
										padding: '3px 12px',
										color: '#10b981',
										fontSize: 12,
										fontWeight: 500,
									}}
								>
									<CheckCircleOutlined style={{ fontSize: 12 }} />
									{step.status}
								</span>
								<span
									style={{
										display: 'inline-flex',
										alignItems: 'center',
										gap: 5,
										background: '#2a2a2a',
										border: '1px solid #3f3f3f',
										borderRadius: 20,
										padding: '3px 12px',
										color: '#10b981',
										fontSize: 12,
										fontWeight: 500,
									}}
								>
									<CheckCircleOutlined style={{ fontSize: 12 }} />
									{step.type}
								</span>
								<DurationBadge startedAt={step.started_at} completedAt={step.completed_at} />
							</div>
						</div>
					</div>
					{step.description && (
						<Text style={{ color: '#8b949e', fontSize: 12, display: 'block', marginBottom: 4 }}>
							{step.description}
						</Text>
					)}
					{step.started_at && (
						<Text style={{ color: '#555', fontSize: 11, display: 'block' }}>
							Started: {new Date(step.started_at).toLocaleTimeString()}
							{step.completed_at && <> · Ended: {new Date(step.completed_at).toLocaleTimeString()}</>}
						</Text>
					)}
				</div>

				<Divider style={{ borderColor: '#2a2a2a', margin: '10px 0' }} />

				{/* Error */}
				{step.error_message && (
					<div className="praxys-computer-section">
						<div className="praxys-computer-label">Error</div>
						<div className="praxys-computer-error">{step.error_message}</div>
					</div>
				)}

				{/* Input */}
				{!isEmpty(input) && (
					<div className="praxys-computer-section">
						<div className="praxys-computer-label">Input</div>
						<JsonViewer data={input} label="Show input" />
					</div>
				)}

				{/* Output */}
				{!isEmpty(output) && (
					<div className="praxys-computer-section">
						<div className="praxys-computer-label">Output</div>
						<JsonViewer data={output} label="Show output" />
					</div>
				)}

				{/* Tool calls for this step */}
				{stepToolCalls.length > 0 && (
					<div className="praxys-computer-section">
						<div className="praxys-computer-label">Tool Calls ({stepToolCalls.length})</div>
						{stepToolCalls.map(tc => {
							let args: any = null;
							let result: any = null;
							try { args = JSON.parse(tc.arguments_json || '{}'); } catch { }
							try { result = JSON.parse(tc.result_json || '{}'); } catch { }
							return (
								<Card
									key={tc.id}
									size="small"
									style={{ background: '#161b22', borderColor: '#2a2a2a', marginBottom: 8 }}
									bodyStyle={{ padding: '8px 12px' }}
								>
									<Text style={{ color: '#c9d1d9', fontSize: 12, fontWeight: 600 }}>
										{tc.tool_name}
									</Text>
									{args && !isEmpty(args) && (
										<div style={{ marginTop: 4 }}>
											<JsonViewer data={args} label="Show args" />
										</div>
									)}
									{result && !isEmpty(result) && (
										<div style={{ marginTop: 4 }}>
											<JsonViewer data={result} label="Show result" />
										</div>
									)}
									{tc.error_message && (
										<div className="praxys-computer-error" style={{ marginTop: 4 }}>
											{tc.error_message}
										</div>
									)}
								</Card>
							);
						})}
					</div>
				)}

				{/* Artifacts for this step */}
				{stepArtifacts.length > 0 && (
					<div className="praxys-computer-section">
						<div className="praxys-computer-label">Artifacts ({stepArtifacts.length})</div>
						<ArtifactPanel artifacts={stepArtifacts} userId={userId} />
					</div>
				)}

				{/* Workspace */}
				<div className="praxys-computer-section">
					<div className="praxys-computer-label">Workspace</div>
					{hasSshCalls ? (
						<SshTerminalView toolCalls={stepToolCalls} events={events} stepId={step.id} />
					) : (
						<AgentWorkspaceView
							taskId={taskId}
							taskStatus={taskStatus}
							onPause={onPause}
							onResume={onResume}
						/>
					)}
				</div>
			</div>
		</div>
	);
};

// ── Main component ────────────────────────────────────────────────────────────

const AgentActivityConsole: React.FC<Props> = ({ taskId, onBack, onOpenTask, user, onLogout, availableKbs = [], recentTasks = [], onScheduleTask }) => {
	// ── Core state ─────────────────────────────────────────────────────────────
	const [task, setTask] = useState<AgentTask | null>(null);
	const [plan, setPlan] = useState<AgentPlan | null>(null);
	const [planData, setPlanData] = useState<PlanData | null>(null);
	const [steps, setSteps] = useState<AgentStep[]>([]);
	const [events, setEvents] = useState<TaskEvent[]>([]);
	const [toolCalls, setToolCalls] = useState<ToolCallLog[]>([]);
	const [artifacts, setArtifacts] = useState<Artifact[]>([]);
	const [followUps, setFollowUps] = useState<TaskFollowUp[]>([]);
	const [subtasks, setSubtasks] = useState<AgentTask[]>([]);
	const [libraryItems, setLibraryItems] = useState<TaskLibraryItem[]>([]);
	const [followUpMessage, setFollowUpMessage] = useState('');
	const [followUpSubmitting, setFollowUpSubmitting] = useState(false);
	const [followUpKbIds, setFollowUpKbIds] = useState<string[]>([]);
	const [followUpPromptMode, setFollowUpPromptMode] = useState<'standard' | 'deep_search'>('standard');
	const [followUpAgentType, setFollowUpAgentType] = useState<string>('autonomous');
	const [followUpDictating, setFollowUpDictating] = useState(false);
	const [followUpSuggestedTasks, setFollowUpSuggestedTasks] = useState<SuggestedTaskStarter[]>([]);
	const [followUpLoadingSuggested, setFollowUpLoadingSuggested] = useState(false);
	const followUpInitRef = useRef<string>('');
	const followUpRecognitionRef = useRef<any>(null);
	const [sourcesDrawerOpen, setSourcesDrawerOpen] = useState(false);
	const [libraryDrawerOpen, setLibraryDrawerOpen] = useState(false);
	const [collapsedThreadTaskIds, setCollapsedThreadTaskIds] = useState<Set<string>>(new Set());
	const [collapsedLibraryTaskIds, setCollapsedLibraryTaskIds] = useState<Set<string>>(new Set());
	const [convertingProject, setConvertingProject] = useState(false);
	const [convertedProject, setConvertedProject] = useState<Project | null>(null);
	const [loading, setLoading] = useState(true);

	// Praxys's Computer panel state
	const [selectedStep, setSelectedStep] = useState<AgentStep | null>(null);
	const [computerOpen, setComputerOpen] = useState(false);

	const esRef = useRef<EventSource | null>(null);
	const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

	// ── Initial load ───────────────────────────────────────────────────────────
	const refresh = useCallback(async (): Promise<boolean> => {
		try {
			const [taskRes, stepsRes, artifactsRes, toolCallsRes, followUpsRes, subtasksRes, libraryRes, activityRes] =
				await Promise.allSettled([
					api.getTask(taskId),
					api.getTaskSteps(taskId),
					api.getTaskArtifacts(taskId),
					api.getTaskToolCalls(taskId),
					api.getTaskFollowUps(taskId),
					api.getTaskSubtasks(taskId),
					api.getTaskLibrary(taskId),
					api.getTaskActivity(taskId),
				]);

			if (taskRes.status === 'fulfilled') setTask(taskRes.value.data);
			if (stepsRes.status === 'fulfilled') setSteps(stepsRes.value.data.steps || []);
			if (artifactsRes.status === 'fulfilled') setArtifacts(artifactsRes.value.data.artifacts || []);
			if (toolCallsRes.status === 'fulfilled') setToolCalls(toolCallsRes.value.data.tool_calls || []);
			if (followUpsRes.status === 'fulfilled') setFollowUps(followUpsRes.value.data.follow_ups || []);
			if (subtasksRes.status === 'fulfilled') setSubtasks(subtasksRes.value.data.subtasks || []);
			if (libraryRes.status === 'fulfilled') setLibraryItems(libraryRes.value.data.library_items || []);
			if (activityRes.status === 'fulfilled') {
				const activities = activityRes.value.data.activities || [];
				setEvents(activities.map((act: any) => ({
					id: act.id,
					event: act.event_type,
					task_id: act.task_id,
					step_id: act.step_id,
					title: act.title,
					message: act.message,
					safe_summary: act.safe_summary,
					status: act.status,
					duration_ms: act.duration_ms,
					data: (() => {
						try { return JSON.parse(act.metadata_json || '{}'); } catch { return {}; }
					})(),
					timestamp: act.created_at,
				})));
			}

			try {
				const planRes = await api.getTaskPlan(taskId);
				const p: AgentPlan = planRes.data;
				setPlan(p);
				try { setPlanData(JSON.parse(p.plan_json || '{}')); } catch { /* plan_json may be absent */ }
			} catch { /* task may not have a plan yet */ }

			return taskRes.status === 'fulfilled';
		} catch (err) {
			message.error('Failed to load task details');
			console.error('[AgentActivityConsole] refresh error:', err);
			return false;
		}
	}, [taskId]);

	useEffect(() => {
		let cancelled = false;
		let retryTimer: ReturnType<typeof setTimeout> | null = null;
		setLoading(true);

		const tryLoad = async (retriesLeft: number) => {
			const found = await refresh();
			if (cancelled) return;
			if (!found && retriesLeft > 0) {
				// Task not found on first attempt — retry after a short delay.
				// This handles timing races where the task was just created.
				retryTimer = setTimeout(() => { if (!cancelled) tryLoad(retriesLeft - 1); }, 1500);
			} else {
				setLoading(false);
			}
		};

		tryLoad(2);

		return () => {
			cancelled = true;
			if (retryTimer) clearTimeout(retryTimer);
		};
	}, [refresh]);

	// ── SSE stream ─────────────────────────────────────────────────────────────
	useEffect(() => {
		if (!ENABLE_TASK_SSE) return undefined;
		let retryDelay = 2000;
		let retryTimer: ReturnType<typeof setTimeout> | null = null;
		let closed = false;

		const openStream = () => {
			if (closed) return;
			if (esRef.current) { esRef.current.close(); esRef.current = null; }
			const es = api.streamTaskEvents(taskId);
			esRef.current = es;
			es.onopen = () => { retryDelay = 2000; };
			es.onmessage = (e) => {
				try {
					const evt: TaskEvent = JSON.parse(e.data);
					if (['connected'].includes(evt.event)) return;
					setEvents(prev => {
						if (evt.id && prev.some(p => p.id === evt.id)) return prev;
						return [...prev.slice(-400), evt];
					});
					const refreshTriggers = [
						'step.completed', 'step.failed', 'task.completed', 'task.failed',
						'task.cancelled', 'artifact.created',
						'plan.approved', 'plan.ready',
						'tool.completed', 'tool.failed',
					];
					if (refreshTriggers.includes(evt.event)) refresh();
				} catch { }
			};
			es.onerror = () => {
				es.close();
				esRef.current = null;
				retryDelay = Math.min(retryDelay * 2, 60_000);
				retryTimer = setTimeout(openStream, retryDelay);
			};
		};

		openStream();
		return () => {
			closed = true;
			if (retryTimer) clearTimeout(retryTimer);
			esRef.current?.close();
			esRef.current = null;
		};
	}, [taskId, refresh]);

	// ── Polling fallback ───────────────────────────────────────────────────────
	useEffect(() => {
		if (pollRef.current) clearInterval(pollRef.current);
		if (!task) return;
		const interval = ACTIVE_STATUSES.has(task.status) ? 2000 : 10000;
		pollRef.current = setInterval(refresh, interval);
		return () => { if (pollRef.current) clearInterval(pollRef.current); };
	}, [task?.status, refresh]);

	// ── Plan / task control ───────────────────────────────────────────────────
	const handleGeneratePlan = async () => {
		if (!task) return;
		try { await api.generatePlan(task.id, true); await refresh(); message.success('Plan generated'); }
		catch (err) { message.error('Failed to generate plan'); console.error(err); }
	};

	const handleStart = async () => {
		if (!task) return;
		try { await api.startTask(task.id); await refresh(); message.success('Task started'); }
		catch (err) { message.error('Failed to start task'); console.error(err); }
	};

	const handlePause = async () => {
		if (!task) return;
		try { await api.pauseTask(task.id); await refresh(); }
		catch (err) { message.error('Failed to pause task'); console.error(err); }
	};

	const handleResume = async () => {
		if (!task) return;
		try { await api.resumeTask(task.id); await refresh(); }
		catch (err) { message.error('Failed to resume task'); console.error(err); }
	};

	const handleCancel = async () => {
		if (!task) return;
		try { await api.cancelTask(task.id); await refresh(); }
		catch (err) { message.error('Failed to cancel task'); console.error(err); }
	};

	const handleSubmitFollowUp = async () => {
		const text = followUpMessage.trim();
		if (!text || !task) return;
		let inputContext: Record<string, any> = {};
		try {
			inputContext = JSON.parse(task.input_context_json || '{}');
		} catch {
			inputContext = {};
		}
		// User-selected mode/KBs override the inherited values from the parent task.
		inputContext = { ...inputContext, prompt_mode: followUpPromptMode };
		setFollowUpSubmitting(true);
		try {
			await api.createTaskFollowUp(taskId, text, {
				inputContext,
				knowledgeBaseIds: followUpKbIds,
				promptMode: followUpPromptMode,
				agentType: followUpAgentType || task.agent_type || '',
				agentTemplateId: task.agent_template_id || '',
			});
			setFollowUpMessage('');
			await refresh();
			message.success('Follow-up submitted');
		} catch (err: any) {
			const detail = err?.response?.data?.detail;
			message.error(`Follow-up failed${detail ? `: ${typeof detail === 'string' ? detail : 'request error'}` : ''}`);
		} finally {
			setFollowUpSubmitting(false);
		}
	};

	// Initialise the follow-up composer (KBs + prompt mode) from the parent task
	// the first time the task loads. Subsequent edits by the user are preserved.
	useEffect(() => {
		if (!task || followUpInitRef.current === task.id) return;
		followUpInitRef.current = task.id;
		try {
			const parsed = JSON.parse(task.knowledge_base_ids_json || '[]');
			if (Array.isArray(parsed)) {
				setFollowUpKbIds(parsed.filter((v): v is string => typeof v === 'string'));
			}
		} catch { /* ignore */ }
		try {
			const ctx = JSON.parse(task.input_context_json || '{}');
			const mode = String(ctx?.prompt_mode || '').toLowerCase();
			setFollowUpPromptMode(mode === 'deep_search' ? 'deep_search' : 'standard');
		} catch { /* ignore */ }
		const inheritedAgentType = String(task.agent_type || '').toLowerCase();
		setFollowUpAgentType(inheritedAgentType === 'supervisor' ? 'supervisor' : 'autonomous');
	}, [task]);

	// Load suggested tasks for the currently selected follow-up Knowledge Bases.
	useEffect(() => {
		let cancelled = false;
		const load = async () => {
			if (followUpKbIds.length === 0) {
				setFollowUpSuggestedTasks([]);
				return;
			}
			setFollowUpLoadingSuggested(true);
			try {
				const responses = await Promise.all(
					followUpKbIds.map(kbId => api.listKnowledgeBaseSuggestedTasks(kbId, { limit: 30 })),
				);
				const merged = responses.flatMap(res => res.data.suggested_tasks || []);
				const deduped = Array.from(
					new Map(merged.map((row: SuggestedTaskStarter) => [row.id, row])).values(),
				);
				if (!cancelled) setFollowUpSuggestedTasks(deduped);
			} catch {
				if (!cancelled) setFollowUpSuggestedTasks([]);
			} finally {
				if (!cancelled) setFollowUpLoadingSuggested(false);
			}
		};
		void load();
		return () => { cancelled = true; };
	}, [followUpKbIds]);

	const handleFollowUpClear = useCallback(() => {
		setFollowUpMessage('');
		setFollowUpKbIds([]);
		setFollowUpPromptMode('standard');
		setFollowUpAgentType('autonomous');
	}, []);

	const handleFollowUpDictationToggle = useCallback(() => {
		const recognizerWindow = window as unknown as {
			SpeechRecognition?: new () => any;
			webkitSpeechRecognition?: new () => any;
		};
		const Ctor = recognizerWindow.SpeechRecognition || recognizerWindow.webkitSpeechRecognition;
		if (!Ctor) {
			message.warning('Dictation is not supported in this browser.');
			return;
		}
		if (followUpRecognitionRef.current) {
			try { followUpRecognitionRef.current.stop(); } catch { /* no-op */ }
			followUpRecognitionRef.current = null;
			setFollowUpDictating(false);
			return;
		}
		try {
			const recognizer = new Ctor();
			recognizer.lang = 'en-US';
			recognizer.interimResults = false;
			recognizer.continuous = true;
			recognizer.onresult = (event: any) => {
				const chunks: string[] = [];
				for (let i = event.resultIndex ?? 0; i < event.results.length; i += 1) {
					const r = event.results[i];
					if (r && r[0] && r.isFinal) chunks.push(String(r[0].transcript || '').trim());
				}
				const spoken = chunks.filter(Boolean).join(' ').trim();
				if (!spoken) return;
				setFollowUpMessage(prev => (prev.trim() ? `${prev.trim()} ${spoken}` : spoken));
			};
			recognizer.onerror = () => {
				setFollowUpDictating(false);
				followUpRecognitionRef.current = null;
			};
			recognizer.onend = () => {
				setFollowUpDictating(false);
				followUpRecognitionRef.current = null;
			};
			followUpRecognitionRef.current = recognizer;
			recognizer.start();
			setFollowUpDictating(true);
		} catch {
			message.error('Could not start dictation');
			setFollowUpDictating(false);
		}
	}, []);

	// Stop dictation if the component unmounts.
	useEffect(() => () => {
		if (followUpRecognitionRef.current) {
			try { followUpRecognitionRef.current.stop(); } catch { /* no-op */ }
			followUpRecognitionRef.current = null;
		}
	}, []);

	const handleConvertToProject = async () => {
		if (!task) return;
		Modal.confirm({
			className: 'praxys-dark-confirm-modal',
			title: 'Convert to Project',
			content: 'This will create a project and move this task, subtasks, and library items into it.',
			okText: 'Convert to Project',
			onOk: async () => {
				setConvertingProject(true);
				try {
					const res = await api.convertTaskToProject(task.id);
					setConvertedProject(res.data);
					await refresh();
					message.success('Project created');
				} catch {
					message.error('Failed to convert task to project');
				} finally {
					setConvertingProject(false);
				}
			},
		});
	};

	// ── Auto-start when plan is ready ─────────────────────────────────────────
	// (Auto-start removed intentionally — tasks must be started manually.)

	// ── Step click → open computer panel ──────────────────────────────────────
	const handleStepClick = (step: AgentStep) => {
		setSelectedStep(step);
		setComputerOpen(true);
	};

	// ── Derived ────────────────────────────────────────────────────────────────
	const stepToolCalls = selectedStep
		? toolCalls.filter(tc => tc.step_id === selectedStep.id)
		: [];
	const stepArtifacts = selectedStep
		? artifacts.filter(a => a.step_id === selectedStep.id)
		: [];
	const sourceEntries = steps.flatMap((step) => {
		try {
			const output = JSON.parse(step.output_json || '{}');
			const results = Array.isArray(output?.results) ? output.results : [];
			return results
				.filter((item: any) => item && (item.url || item.title))
				.map((item: any) => ({
					stepId: step.id,
					stepName: step.name,
					title: item.title || '',
					url: item.url || '',
					snippet: item.snippet || '',
				}));
		} catch {
			return [];
		}
	});
	const isDeepSearchTask = useMemo(() => {
		try {
			const input = JSON.parse(task?.input_context_json || '{}');
			return String(input?.prompt_mode || '').toLowerCase() === 'deep_search';
		} catch {
			return false;
		}
	}, [task?.input_context_json]);
	const subtasksById = useMemo(() => {
		const byId = new Map<string, AgentTask>();
		for (const subtask of subtasks) byId.set(subtask.id, subtask);
		return byId;
	}, [subtasks]);
	const followUpsOrdered = useMemo(
		() => {
			const toMs = (value?: string) => {
				const parsed = Date.parse(value || '');
				return Number.isFinite(parsed) ? parsed : 0;
			};
			return [...followUps].sort((a, b) => toMs(a.created_at) - toMs(b.created_at));
		},
		[followUps],
	);
	const hasFollowUpThread = followUpsOrdered.length > 0;
	const completedResultTasks = useMemo(() => {
		const toMs = (value?: string) => {
			const parsed = Date.parse(value || '');
			return Number.isFinite(parsed) ? parsed : 0;
		};
		const all = [...(task ? [task] : []), ...subtasks];
		return all
			.filter((item) => item.status === 'COMPLETED' && (item.final_answer || '').trim())
			.sort((a, b) => toMs(b.completed_at || b.updated_at) - toMs(a.completed_at || a.updated_at));
	}, [task, subtasks]);
	const deepSearchThread = useMemo(() => {
		if (!task) return [];
		const entries: DeepSearchThreadEntry[] = [];
		entries.push({
			key: task.id,
			title: task.goal,
			createdAt: task.completed_at || task.updated_at,
			content: task.final_answer,
			status: task.status,
			taskId: task.id,
		});
		for (const followUp of followUpsOrdered) {
			const linkedTask = followUp.created_task_id ? subtasksById.get(followUp.created_task_id) : undefined;
			entries.push({
				key: followUp.id,
				title: followUp.message,
				createdAt: linkedTask?.completed_at || followUp.created_at,
				content: linkedTask?.final_answer || followUp.answer,
				status: linkedTask?.status || followUp.status,
				taskId: linkedTask?.id || followUp.created_task_id,
			});
		}
		return entries;
	}, [followUpsOrdered, subtasksById, task]);

	// ── Render ─────────────────────────────────────────────────────────────────
	if (loading) {
		return (
			<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#212121' }}>
				<Spin size="large" />
			</div>
		);
	}

	if (!task) {
		return (
			<div style={{ textAlign: 'center', padding: 40, color: '#555', background: '#212121', height: '100vh' }}>
				Task not found.
			</div>
		);
	}

	const rootTaskId = task.root_task_id || task.id;
	const isRootTask = !task.parent_task_id || task.id === rootTaskId;
	const showFollowUpComposer = isRootTask && TERMINAL_TASK_STATUSES.has(task.status);
	const showStandaloneResultPanel = !isDeepSearchTask || !hasFollowUpThread;
	const canConvertToProject = isRootTask && (subtasks.length > 0 || libraryItems.length > 0) && !task.project_id;
	const taskGoalSummary = summarizeTaskPrompt(task.goal);
	const downloadTaskMarkdown = (targetTask: AgentTask) => {
		const blob = new Blob([targetTask.final_answer ?? ''], { type: 'text/markdown;charset=utf-8' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		const safeName = targetTask.goal
			.slice(0, MAX_DOWNLOAD_FILENAME_LENGTH)
			.replace(/[^a-z0-9_\- ]/gi, '_')
			.replace(/^[-_ ]+|[-_ ]+$/g, '') || 'praxys-result';
		a.download = `${safeName}.md`;
		a.click();
		URL.revokeObjectURL(url);
	};
	const downloadTaskDocx = (targetTask: AgentTask) => {
		const safeId = targetTask.id.replace(/[^a-zA-Z0-9_-]/g, '_');
		const a = document.createElement('a');
		a.href = `${api.TASK_SERVICE_URL}/tasks/${targetTask.id}/export/docx`;
		a.download = `praxys-result-${safeId}.docx`;
		a.click();
	};
	const downloadTaskPdf = (targetTask: AgentTask) => {
		const safeId = targetTask.id.replace(/[^a-zA-Z0-9_-]/g, '_');
		const a = document.createElement('a');
		a.href = `${api.TASK_SERVICE_URL}/tasks/${targetTask.id}/export/pdf`;
		a.download = `praxys-result-${safeId}.pdf`;
		a.click();
	};
	const shareTaskResult = async (targetTask: AgentTask) => {
		const taskUrl = `${window.location.origin}/tasks/${targetTask.id}`;
		try {
			if (navigator.share) {
				await navigator.share({
					title: targetTask.goal,
					text: targetTask.final_answer?.slice(0, MAX_SHARE_TEXT_LENGTH) || 'Task result',
					url: taskUrl,
				});
				return;
			}
			await navigator.clipboard.writeText(taskUrl);
			message.success('Task link copied');
		} catch {
			message.error('Unable to share this result');
		}
	};

	return (
		<div className="praxys-console-shell">
			{/* ── Header ──────────────────────────────────────────────────────────── */}
			<TaskStatusHeader
				task={task}
				onBack={onBack}
				onStart={handleStart}
				onPause={handlePause}
				onResume={handleResume}
				onCancel={handleCancel}
				onRefreshPlan={handleGeneratePlan}
			/>

			{/* ── Content area ────────────────────────────────────────────────────── */}
			<div className="praxys-console-content">
				{/* Center column */}
				<div className="praxys-console-center-wrap">
					<div className="praxys-console-center">
						{/* Mission statement header */}
						<div className="praxys-task-mission">
							<img src={logoHandBtn} alt="Praxys" className="praxys-task-mission-logo" />
							<p className="praxys-task-mission-text" title={task.goal}>{taskGoalSummary}</p>
						</div>
						<div style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
							{canConvertToProject && !isDeepSearchTask && (
								<Button className="praxys-toolbar-pill" size="small" loading={convertingProject} onClick={handleConvertToProject}>
									Convert to Project
								</Button>
							)}
							{(convertedProject || task.project_id) && (
								<a href={`/projects/${(convertedProject?.id || task.project_id)}`} style={{ color: '#58a6ff', fontSize: 12 }}>
									Open project workspace
								</a>
							)}
						</div>
						<div className="praxys-task-mission-steps">
							<AgentStepTimeline
								steps={steps}
								planSteps={planData?.steps}
								taskStatus={task.status}
								onStepClick={handleStepClick}
							/>
						</div>
						{showStandaloneResultPanel && (
							<div className="praxys-task-mission-steps">
								<FinalResultPanel task={task} steps={steps} artifacts={artifacts} toolCalls={toolCalls} />
							</div>
						)}
						{isDeepSearchTask && (
							<div className="praxys-deep-search-thread">
								<div className="praxys-deep-search-toolbar">
									{canConvertToProject && (
										<Button
											className="praxys-toolbar-pill praxys-deep-search-toolbar-pill"
											size="small"
											loading={convertingProject}
											onClick={handleConvertToProject}
										>
											Convert to Project
										</Button>
									)}
									<Button className="praxys-toolbar-pill praxys-deep-search-toolbar-pill" size="small" onClick={() => setLibraryDrawerOpen(true)}>
										Library ({completedResultTasks.length})
									</Button>
									<Button className="praxys-toolbar-pill praxys-deep-search-toolbar-pill" size="small" onClick={() => setSourcesDrawerOpen(true)}>
										Source ({sourceEntries.length})
									</Button>
								</div>
								{hasFollowUpThread && (
									<List
										className="praxys-deep-search-thread-list"
										dataSource={deepSearchThread}
										renderItem={(entry) => {
											const collapseKey = entry.taskId || entry.key;
											const expanded = !collapsedThreadTaskIds.has(collapseKey);
											return (
												<List.Item>
													<div className="praxys-deep-search-thread-item">
														<div
															className="praxys-deep-search-thread-item-head"
															onClick={() => setCollapsedThreadTaskIds((prev) => {
																const next = new Set(prev);
																if (next.has(collapseKey)) next.delete(collapseKey); else next.add(collapseKey);
																return next;
															})}
														>
															{expanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
															<div className="praxys-deep-search-thread-item-title-wrap">
																<div className="praxys-deep-search-thread-item-title">{entry.title}</div>
																<div className="praxys-deep-search-thread-item-meta">
																	<Tag>{entry.status || 'pending'}</Tag>
																	{entry.createdAt ? new Date(entry.createdAt).toLocaleString() : ''}
																</div>
															</div>
															{entry.taskId && entry.taskId !== task.id && onOpenTask && (
																<Button
																	className="praxys-toolbar-pill praxys-deep-search-thread-action"
																	size="small"
																	onClick={(event) => {
																		event.stopPropagation();
																		onOpenTask(entry.taskId || '');
																	}}
																>
																	Open task
																</Button>
															)}
														</div>
														{expanded && (
															<div className="praxys-deep-search-thread-item-body">
																{entry.content ? (
																	<ReactMarkdown remarkPlugins={[remarkGfm]}>{entry.content}</ReactMarkdown>
																) : (
																	<Text style={{ color: '#8b949e', fontSize: 12 }}>
																		{TERMINAL_TASK_STATUSES.has(entry.status || '')
																			? 'No result was generated for this task.'
																			: 'Result is still being generated.'}
																	</Text>
																)}
															</div>
														)}
													</div>
												</List.Item>
											);
										}}
									/>
								)}
							</div>
						)}
						{showFollowUpComposer && (
							<div className="praxys-input-wrap praxys-task-followup-composer">
								<div className={`praxys-input-box${followUpDictating ? ' praxys-input-box--dictation' : ''}`}>
									<Text className="praxys-deep-search-composer-label" style={{ display: 'block', padding: '10px 12px 0' }}>
										Follow-up — continue this task
									</Text>
									<div className="praxys-task-starter-row">
										<button
											type="button"
											className="praxys-task-clear-btn"
											onClick={handleFollowUpClear}
											title="Clear input"
											aria-label="Clear input"
										>
											<CloseCircleOutlined />
										</button>
										{followUpKbIds.length > 0 && (
											<div className="praxys-suggested-task-wrap">
												<span className="praxys-suggested-task-label">Use Suggested Task</span>
												<Select
													size="small"
													bordered={false}
													className="praxys-task-starter-select"
													placeholder="Use Suggested Task"
													loading={followUpLoadingSuggested}
													value={undefined}
													onSelect={(selectedId: string) => {
														const selected = followUpSuggestedTasks.find(s => s.id === selectedId);
														if (!selected) return;
														const selectedPrompt = (selected.suggested_prompt || selected.description || selected.name || '').trim();
														setFollowUpMessage(selectedPrompt);
														if (selected.recommended_mode === 'deep_search') setFollowUpPromptMode('deep_search');
														else setFollowUpPromptMode('standard');
														if (selected.suggested_knowledge_base_ids && selected.suggested_knowledge_base_ids.length > 0) {
															setFollowUpKbIds(selected.suggested_knowledge_base_ids);
														}
													}}
													options={followUpSuggestedTasks.map(t => ({
														value: t.id,
														label: `${t.name}${t.recommended_mode === 'deep_search' ? ' · Deep Search' : ''}`,
													}))}
													notFoundContent="No suggested tasks yet"
													style={{ minWidth: 170, maxWidth: 240 }}
												/>
											</div>
										)}
										{recentTasks.length > 0 && (
											<Select
												size="small"
												bordered={false}
												className="praxys-task-starter-select"
												placeholder="Use Recent Task"
												value={undefined}
												onSelect={(rtId: string) => {
													const recent = recentTasks.find(t => t.id === rtId);
													if (recent) {
														setFollowUpMessage(recent.goal || '');
														setFollowUpPromptMode('standard');
													}
												}}
												options={recentTasks.map(t => ({
													value: t.id,
													label: t.goal.length > 80 ? `${t.goal.slice(0, 80)}…` : t.goal,
												}))}
												notFoundContent="No recent tasks"
												style={{ minWidth: 150, maxWidth: 230 }}
											/>
										)}
									</div>
									<textarea
										className="praxys-textarea"
										value={followUpMessage}
										onChange={(e) => setFollowUpMessage(e.target.value)}
										onKeyDown={(e) => {
											if (e.key === 'Enter' && !e.shiftKey) {
												e.preventDefault();
												if (!followUpSubmitting && followUpMessage.trim()) handleSubmitFollowUp();
											}
										}}
										placeholder="Describe what you want the agent to do next…"
										rows={2}
										disabled={followUpSubmitting}
									/>
									<div className="praxys-input-footer">
										<div className="praxys-input-footer-left">
											<Select
												value={followUpAgentType}
												onChange={setFollowUpAgentType}
												size="small"
												bordered={false}
												options={[
													{ value: 'autonomous', label: 'Autonomous' },
													{ value: 'supervisor', label: 'Supervisor' },
												]}
												className="praxys-agent-select"
											/>
											{availableKbs.length > 0 && (
												<Select
													mode="multiple"
													size="small"
													placeholder="+ Knowledge Bases"
													value={followUpKbIds}
													onChange={setFollowUpKbIds}
													bordered={false}
													style={{ minWidth: 160, maxWidth: 300, fontSize: 12 }}
													className="praxys-kb-select"
													dropdownClassName="praxys-kb-select-dropdown"
													options={availableKbs.map(kb => ({
														value: kb.id,
														label: (
															<div className="praxys-kb-option">
																<span className="praxys-kb-option-name">{kb.name}</span>
																<span className="praxys-kb-option-meta">
																	{kb.document_count || 0} docs · {kb.chunk_count || 0} chunks
																</span>
															</div>
														),
														title: kb.description,
													}))}
													maxTagCount={2}
												/>
											)}
										</div>
										<div className="praxys-input-footer-right">
											{followUpPromptMode === 'deep_search' && (
												<span className="praxys-deep-research-pill">
													<span>Deep Research</span>
													<button
														type="button"
														className="praxys-deep-research-pill-remove"
														onClick={() => setFollowUpPromptMode('standard')}
														aria-label="Remove Deep Research mode"
														title="Remove Deep Research mode"
													>
														×
													</button>
												</span>
											)}
											<button
												className={`praxys-dictation-btn${followUpDictating ? ' active' : ''}`}
												onClick={handleFollowUpDictationToggle}
												title={followUpDictating ? 'Stop dictation' : 'Dictate follow-up'}
												aria-label={followUpDictating ? 'Stop dictation' : 'Dictate follow-up'}
												type="button"
											>
												<AudioOutlined style={{ fontSize: 14 }} />
											</button>
											<Dropdown
												trigger={['click']}
												placement="topRight"
												overlay={
													<div className="praxys-more-menu ant-dropdown-menu ant-dropdown-menu-root ant-dropdown-menu-vertical">
														<div
															className="ant-dropdown-menu-item"
															role="menuitem"
															onClick={() => setFollowUpPromptMode(prev => (prev === 'deep_search' ? 'standard' : 'deep_search'))}
														>
															<SearchOutlined style={{ marginRight: 8 }} />
															Deep Research{followUpPromptMode === 'deep_search' ? ' ✓' : ''}
														</div>
														{onScheduleTask && (
															<div
																className="ant-dropdown-menu-item"
																role="menuitem"
																onClick={() => onScheduleTask(followUpMessage.trim())}
															>
																<CalendarOutlined style={{ marginRight: 8 }} />
																Schedule Task
															</div>
														)}
													</div>
												}
											>
												<button className="praxys-more-btn" aria-label="More options" type="button">
													<MoreOutlined style={{ fontSize: 16 }} />
												</button>
											</Dropdown>
											<button
												className={`praxys-send-btn${followUpMessage.trim() ? ' active' : ''}`}
												onClick={handleSubmitFollowUp}
												disabled={followUpSubmitting || !followUpMessage.trim()}
												aria-label="Send follow-up"
												type="button"
											>
												{followUpSubmitting ? <span className="praxys-spinner" /> : <SendOutlined style={{ fontSize: 14 }} />}
											</button>
										</div>
									</div>
								</div>
								<p className="praxys-hint">
									Press <kbd className="praxys-kbd">Enter</kbd> to submit,&nbsp;
									<kbd className="praxys-kbd">Shift+Enter</kbd> for a new line
								</p>
							</div>
						)}
						{isDeepSearchTask && (
							<>
								<Drawer
									className="praxys-deep-search-drawer"
									title={<span className="praxys-deep-search-drawer-title">Sources ({sourceEntries.length})</span>}
									placement="right"
									width={540}
									open={sourcesDrawerOpen}
									onClose={() => setSourcesDrawerOpen(false)}
									closable
									closeIcon={<span aria-hidden>✕</span>}
								>
									{sourceEntries.length === 0 ? (
										<Empty description="No sources captured yet" />
									) : (
										<List
											className="praxys-deep-search-sources-list"
											dataSource={sourceEntries}
											renderItem={(row: any) => (
												<List.Item className="praxys-deep-search-drawer-list-item">
													<div className="praxys-deep-search-source-card">
														<div className="praxys-deep-search-source-step">{row.stepName}</div>
														<div className="praxys-deep-search-source-title">{row.title || row.url}</div>
														{row.url && (
															<a href={row.url} target="_blank" rel="noreferrer" className="praxys-deep-search-source-link">
																{row.url}
															</a>
														)}
														{row.snippet && <div className="praxys-deep-search-source-snippet">{row.snippet}</div>}
													</div>
												</List.Item>
											)}
										/>
									)}
								</Drawer>
								<Drawer
									className="praxys-deep-search-drawer"
									title={<span className="praxys-deep-search-drawer-title">Library ({completedResultTasks.length})</span>}
									placement="right"
									width={640}
									open={libraryDrawerOpen}
									onClose={() => setLibraryDrawerOpen(false)}
									closable
									closeIcon={<span aria-hidden>✕</span>}
								>
									{completedResultTasks.length === 0 ? (
										<Empty description="No completed task results yet" />
									) : (
										<div>
											{completedResultTasks.map((libraryTask) => {
												const expanded = !collapsedLibraryTaskIds.has(libraryTask.id);
												return (
													<div key={libraryTask.id} className="praxys-deep-search-library-card">
														<div
															className="praxys-deep-search-library-card-head"
															onClick={() => setCollapsedLibraryTaskIds((prev) => {
																const next = new Set(prev);
																if (next.has(libraryTask.id)) next.delete(libraryTask.id); else next.add(libraryTask.id);
																return next;
															})}
														>
															{expanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
															<div className="praxys-deep-search-library-card-meta">
																<div className="praxys-deep-search-library-card-title">{libraryTask.goal}</div>
																<div className="praxys-deep-search-library-card-date">
																	{libraryTask.completed_at ? new Date(libraryTask.completed_at).toLocaleString() : ''}
																</div>
															</div>
															<Dropdown
																menu={{
																	items: [
																		{ key: 'md', icon: <FileTextOutlined />, label: 'Markdown (.md)' },
																		{ key: 'docx', icon: <FileWordOutlined />, label: 'Word (.docx)' },
																		{ key: 'pdf', icon: <FilePdfOutlined />, label: 'PDF' },
																		{ key: 'share', icon: <ShareAltOutlined />, label: 'Share' },
																	],
																	onClick: ({ key, domEvent }) => {
																		domEvent.stopPropagation();
																		if (key === 'md') downloadTaskMarkdown(libraryTask);
																		if (key === 'docx') downloadTaskDocx(libraryTask);
																		if (key === 'pdf') downloadTaskPdf(libraryTask);
																		if (key === 'share') void shareTaskResult(libraryTask);
																	},
																}}
																trigger={['click']}
																placement="bottomRight"
															>
																<Button
																	className="praxys-deep-search-library-menu-btn"
																	size="small"
																	icon={<MoreOutlined />}
																	aria-label="Download options"
																	onClick={(event) => event.stopPropagation()}
																/>
															</Dropdown>
															<span onClick={(event) => event.stopPropagation()}>
																<AddToKnowledgeBaseButton
																	userId={task.user_id}
																	size="small"
																	draft={{
																		suggestedTitle: libraryTask.goal?.slice(0, 120) || 'Research Result',
																		summary: (libraryTask.final_answer || '').slice(0, 500),
																		contentMarkdown: libraryTask.final_answer || '',
																		sourceModule: 'deep_research_result',
																		sourceId: libraryTask.id,
																		tags: ['research-result'],
																		metadata: {
																			task_id: libraryTask.id,
																			status: libraryTask.status,
																			completed_at: libraryTask.completed_at,
																		},
																	}}
																/>
															</span>
														</div>
														{expanded && (
															<div className="praxys-deep-search-library-card-body praxys-library-item-result">
																<ReactMarkdown remarkPlugins={[remarkGfm]}>{libraryTask.final_answer || ''}</ReactMarkdown>
															</div>
														)}
													</div>
												);
											})}
										</div>
									)}
								</Drawer>
							</>
						)}
					</div>
				</div>

				{/* Right panel: Praxys's Computer — slides in from the right */}
				{computerOpen && selectedStep && (
					<ComputerPanel
						step={selectedStep}
						stepToolCalls={stepToolCalls}
						stepArtifacts={stepArtifacts}
						events={events}
						taskId={taskId}
						userId={task.user_id}
						taskStatus={task.status}
						onClose={() => setComputerOpen(false)}
						onPause={handlePause}
						onResume={handleResume}
					/>
				)}
			</div>
		</div>
	);
};

export default AgentActivityConsole;
